<template>
    <ActionModal
        :method="deleteBookparts()"
        :action-data-adapter="dataAdapter"
        success-text="Sentence(s) deleted"
        :title="'Delete sentence(s)'"
        action-text="Delete"
        :item="data"
        @delete="$emit('delete', $event)"
    >
        Are you sure you want to delete sentence(s)?
        <br />
        <div class="sentence-deleter__sentences">
            <div v-for="item in sentences" :key="item.id">
                {{ item.raw_text }}
            </div>
        </div>
    </ActionModal>
</template>

<script>
import { ActionModal } from '@speechki-frontend/shared/ui/modals';
import { deleteMultipleBookparts } from '@speechki-frontend/core/book/api/books';
import { socket, getSocketConnectionData } from '@speechki-frontend/core/socket';
import { getToken } from '@speechki-frontend/core/auth/utils';

export default {
    name: 'SentenceDelete',
    components: {
        ActionModal
    },
    inject: ['selector'],

    props: {
        bookpart: {
            type: Object,
            required: true
        }
    },

    computed: {
        isSelectionActive() {
            return this.selector.selection.some(item => item.id === this.bookpart.id);
        },
        isSingle() {
            if (!this.isSelectionActive) return true;

            return (
                this.selector.selection.length === 1 ||
                (this.selector.selection.length > 1 &&
                    !this.selector.isInRange(this.selector.sortedSelection[0].id))
            );
        },
        data() {
            if (!this.isSelectionActive) {
                return this.bookpart;
            }

            if (this.isSingle) {
                return this.selector.sortedSelection[0];
            }

            return this.selector.sortedSelection;
        },
        dataAdapter() {
            if (this.isSingle) {
                return data => data.id;
            }

            return data => data.map(bp => bp.id).join(',');
        },
        sentences() {
            if (!this.isSelectionActive) {
                return [{ id: this.bookpart.id, raw_text: this.bookpart.raw_text }];
            }

            return this.selector.sortedSelection.map(item => ({
                id: item.id,
                raw_text: item.bookpart.raw_text
            }));
        }
    },

    methods: {
        deleteBookparts() {
            return data => {
                return new Promise(async (resolve, reject) => {
                    // subscribe before firing the delete request, so a fast worker
                    // can't publish delete_sentence before we start listening —
                    // buffer any operation_ids received before we know our own
                    const ws = socket(getSocketConnectionData(getToken()));
                    const receivedOperationIds = new Set();
                    let operationId = null;
                    let timeout;
                    let settled = false;

                    const off = () => {
                        ws.receiver.off('delete_sentence', handleMessage);
                    };

                    const settle = (result) => {
                        if (settled) return;
                        settled = true;
                        clearTimeout(timeout);
                        off();
                        resolve(result);
                    };

                    function handleMessage({ params = {} }) {
                        if (!params.operation_id) return;

                        if (operationId === null) {
                            receivedOperationIds.add(params.operation_id);
                            return;
                        }

                        if (params.operation_id === operationId) {
                            settle(pendingResult);
                        }
                    }

                    ws.receiver.on('delete_sentence', handleMessage);

                    let pendingResult;
                    try {
                        const result = await deleteMultipleBookparts(
                            this.$store.state.book.book.id,
                            data
                        );

                        pendingResult = result;
                        operationId = result.operation_id;

                        if (receivedOperationIds.has(operationId)) {
                            settle(result);
                            return;
                        }

                        // fallback in case ws message is missed/delayed
                        timeout = setTimeout(() => settle(result), 20000);
                    } catch (e) {
                        off();
                        reject(e);
                    }
                });
            };
        }
    }
};
</script>

<style lang="scss" src="./SentenceDeleter.scss"></style>
